import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  fetchFacebookCampaigns,
  fetchCampaignData,
  exportCampaignData
} from '../services/campaignService';
import {
  sortCampaigns,
  calculateSummaryMetrics,
  precacheCampaignTypes,
  clearCampaignTypeCache,
  getFacebookCampaignData,
  extractFacebookBudget
} from '../utils/campaignUtils';

/**
 * Custom hook to manage campaign data fetching and state
 */
const useCampaignData = (dateFilters, paginationAndSorting, filters, viewState, showError, facebookAccountId = null) => {
  // State for campaign data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [facebookCostData, setFacebookCostData] = useState(null);
  const [adSenseData, setAdSenseData] = useState(null);
  const [compadoData, setCompadoData] = useState(null);
  const [facebookCampaigns, setFacebookCampaigns] = useState({});
  const [combinedData, setCombinedData] = useState(null);
  const [allData, setAllData] = useState(null);

  // Deprecated - kept for backwards compatibility
  const taboolaCampaigns = {};
  const taboolaCostData = null;

  // Get state from props
  const { startDate, endDate } = dateFilters;
  const { platformFilter, groupBy } = filters;
  const { 
    sortField, 
    sortDirection, 
    page, 
    rowsPerPage, 
    updateTotalItems, 
    checkAllDataFetched 
  } = paginationAndSorting;
  
  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!combinedData) return null;
    return calculateSummaryMetrics(combinedData, platformFilter, startDate, endDate, facebookCampaigns);
  }, [combinedData, platformFilter, startDate, endDate, facebookCampaigns]);
  
  // Load initial campaign data (Facebook campaigns only - Taboola removed)
  const loadCampaignsData = async (forceRefresh = false) => {
    try {
      console.log(`Loading campaigns data ${forceRefresh ? '(force refresh)' : ''}`);

      // Facebook campaigns require account_id selection
      // and should be loaded via loadFacebookCampaignsData when user selects an account

      return {
        taboola: {}, // Deprecated - Taboola removed
        facebook: facebookCampaigns
      };
    } catch (error) {
      console.error('Error loading campaigns data:', error);
      showError('Failed to load campaigns data');
      return { taboola: {}, facebook: {} };
    }
  };
  
  // Load only Facebook campaigns data
  const loadFacebookCampaignsData = async (forceRefresh = false, account_id = null) => {
    try {
      console.log(`Loading Facebook campaigns data ${forceRefresh ? '(force refresh)' : ''} for account: ${account_id || 'default'}`);

      // Fetch Facebook campaigns with force refresh parameter and account_id
      // to ensure we get the latest data with budget information
      const facebookResponse = await fetchFacebookCampaigns(forceRefresh, account_id);
      if (facebookResponse.success) {
        const campaignsCount = Object.keys(facebookResponse.data).length;
        console.log(`Loaded ${campaignsCount} Facebook campaigns for account ${account_id || 'default'}`);
        
        // Debug log sample campaign data to analyze budget issues
        if (campaignsCount > 0) {
          const sampleCampaigns = Object.entries(facebookResponse.data).slice(0, 2);
          console.log('Sample Facebook campaign data:', 
            sampleCampaigns.map(([id, campaign]) => ({
              id,
              name: campaign.name,
              daily_budget: campaign.daily_budget,
              lifetime_budget: campaign.lifetime_budget,
              budget_source: campaign.budget_source || 'campaign',
              adset_budget: campaign.adsetBudgets ? 'present' : 'none'
            }))
          );
          
          // Add extra logging for campaign structure
          console.debug('FULL Campaign data structure:', {
            firstCampaign: sampleCampaigns[0] ? sampleCampaigns[0][1] : 'no campaigns',
            hasDirectBudgetProp: sampleCampaigns[0] ? 
              (sampleCampaigns[0][1].daily_budget !== undefined) : false,
            originalResponseStructure: facebookResponse
          });
        }
        
        setFacebookCampaigns(facebookResponse.data);
        return facebookResponse;
      } else {
        console.error('Failed to load Facebook campaigns data:', facebookResponse.error);
        return { success: false, error: facebookResponse.error };
      }
    } catch (error) {
      console.error('Error loading Facebook campaigns data:', error);
      return { success: false, error: error.message };
    }
  };
  
  // Function to remove duplicates from campaign data
  const removeDuplicates = (data) => {
    if (!data || !data.campaigns || !Array.isArray(data.campaigns)) return data;
    
    // Create a Map to store unique campaigns by ID
    const uniqueCampaigns = new Map();
    
    // For each campaign, keep only the last occurrence with the same ID
    data.campaigns.forEach(campaign => {
      if (campaign && campaign.campaign_id) {
        uniqueCampaigns.set(campaign.campaign_id, campaign);
      }
    });
    
    // Convert Map values back to array
    const uniqueCampaignsArray = Array.from(uniqueCampaigns.values());
    
    // Log the difference if duplicates were found
    const diff = data.campaigns.length - uniqueCampaignsArray.length;
    if (diff > 0) {
      console.log(`Removed ${diff} duplicate campaign entries`);
    }
    
    // Return updated data object with duplicates removed
    return {
      ...data,
      campaigns: uniqueCampaignsArray
    };
  };
  
  // Helper function to wait for Facebook data to be available
  const waitForFacebookData = async (maxWaitMs = 3000) => {
    if (!facebookAccountId) return true; // No Facebook data expected
    
    const startTime = Date.now();
    const checkInterval = 100; // Check every 100ms
    
    console.log(`⏳ Waiting for Facebook data to be available for account: ${facebookAccountId}`);
    
    while (Date.now() - startTime < maxWaitMs) {
      if (Object.keys(facebookCampaigns).length > 0) {
        console.log(`✅ Facebook data available after ${Date.now() - startTime}ms (${Object.keys(facebookCampaigns).length} campaigns)`);
        return true;
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    
    console.warn(`⚠️ Facebook data not available after ${maxWaitMs}ms wait for account ${facebookAccountId}`);
    return false;
  };
  
  // Function to fetch report data
  const fetchReport = async (forceRefresh = false, overrideFacebookCampaigns = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use override campaigns if provided, otherwise use state
      const fbCampaignsToUse = overrideFacebookCampaigns || facebookCampaigns;
      
      console.log('🚀 fetchReport starting with:', { 
        startDate, endDate, 
        isSubid3: viewState.isSubid3View, 
        selectedSubid2: viewState.selectedSubid2,
        sortField, sortDirection,
        forceRefresh,
        facebookAccountId,
        facebookCampaignsCount: Object.keys(fbCampaignsToUse).length,
        usingOverride: !!overrideFacebookCampaigns
      });
      
      // Wait for Facebook data only if not using override
      if (!overrideFacebookCampaigns) {
        await waitForFacebookData();
      }
      
      // Clear the campaign type cache on forced refresh
      if (forceRefresh) {
        clearCampaignTypeCache();
      }
      
      const response = await fetchCampaignData(
        startDate,
        endDate,
        filters,
        viewState,
        { sortField, sortDirection },
        { page, rowsPerPage },
        forceRefresh,
        fbCampaignsToUse,
        facebookAccountId
      );
      
      if (response.success) {
        // Ensure we're still in the same view mode before updating state
        // This prevents state updates for a canceled or outdated request
        if (response.viewState) {
          const currentIsSubid3 = viewState.isSubid3View;
          const responseIsSubid3 = response.viewState.isSubid3View;
          
          if (currentIsSubid3 !== responseIsSubid3) {
            console.log('View state changed during fetch, discarding results');
            setLoading(false);
            return { success: false, error: 'View state changed during fetch' };
          }
        }
        
        // Determine if we need to apply client-side sorting for certain fields
        let resultData = response.data;
        
        // Client-side sorting for fields like 'cost', 'profit', 'roas' which are calculated after
        // combining revenue and cost data
        if (response.data.needsClientSideSorting && response.data.clientSortField) {
          console.log(`Applying client-side sorting for ${response.data.clientSortField} ${response.data.clientSortDirection}`);
          resultData = handleClientSideSorting(
            response.data, 
            response.data.clientSortField, 
            response.data.clientSortDirection
          );
        }
        
        console.log(`Received data with ${resultData.campaigns?.length || 0} campaigns`);
        
        // Remove any duplicates that might exist in the data
        resultData = removeDuplicates(resultData);
        
        // Precache campaign types for better performance
        if (resultData.campaigns && resultData.campaigns.length > 0) {
          console.log('Pre-caching campaign types for better performance');
          precacheCampaignTypes(resultData.campaigns, taboolaCampaigns, facebookCampaigns);
        }
        
        // Update state with new data
        setReportData(response.originalRevenueData || null);
        setFacebookCostData(response.facebookCostData || null);
        setAdSenseData(response.adSenseData || null);
        setCompadoData(response.compadoData || null);
        setCombinedData(resultData);

        console.log(`✅ NEW DATA SET: ${resultData?.campaigns?.length || 0} campaigns for account: ${facebookAccountId || 'none'}`);
        
        paginationAndSorting.updateTotalItems(response.totalItems || 0);
        
        // Check if all data is fetched
        const isAllDataFetched = paginationAndSorting.checkAllDataFetched(
          resultData.campaigns ? resultData.campaigns.length : 0,
          response.totalItems
        );
        
        // Update all data cache if needed
        if (isAllDataFetched) {
          setAllData({
            ...resultData,
            campaigns: [...resultData.campaigns]
          });
        }
        
        return { success: true, data: resultData };
      } else {
        setError(response.error || 'Failed to fetch report data');
        showError(response.error || 'Failed to fetch report data');
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      setError(error.message || 'An unexpected error occurred');
      showError(error.message || 'An unexpected error occurred');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };
  
  // Function to fetch report data with a specific view state (for drill-down)
  const fetchReportWithViewState = async (forceRefresh = false, overrideViewState = null) => {
    setLoading(true);
    setError(null);
    
    // Use the override view state if provided, otherwise use the hook's state
    const effectiveViewState = overrideViewState || viewState;
    
    console.log('🚀 fetchReportWithViewState starting with:', { 
      startDate, endDate, 
      viewState: effectiveViewState,
      sortField, sortDirection,
      forceRefresh,
      facebookAccountId,
      facebookCampaignsCount: Object.keys(facebookCampaigns).length
    });
    
    // Wait for Facebook data if expected
    await waitForFacebookData();
    
    // Clear the campaign type cache on forced refresh
    if (forceRefresh) {
      clearCampaignTypeCache();
    }
    
    try {
      // Explicit check for returning to main view
      if (effectiveViewState && effectiveViewState.isSubid3View === false && viewState.isSubid3View) {
        console.log('IMPORTANT: Detected return to main view, using fresh non-subid3 view state');
        // Create a fresh clean main view state object to ensure no subid3 state leaks through
        const cleanMainViewState = {
          isSubid3View: false,
          selectedSubid2: null,
          platformType: null
        };
        
        // Use the clean main view state and force refresh
        const response = await fetchCampaignData(
          startDate,
          endDate,
          filters,
          cleanMainViewState,
          { sortField, sortDirection },
          { page, rowsPerPage },
          true, // Always force refresh when returning to main view
          facebookCampaigns,
          facebookAccountId
        );
        
        if (response.success) {
          // Process the response the same way as below
          let resultData = response.data;
          
          if (response.data.needsClientSideSorting && response.data.clientSortField) {
            console.log(`Applying client-side sorting for ${response.data.clientSortField} ${response.data.clientSortDirection}`);
            resultData = handleClientSideSorting(
              response.data, 
              response.data.clientSortField, 
              response.data.clientSortDirection
            );
          }
          
          // Remove duplicates
          resultData = removeDuplicates(resultData);
          
          console.log(`Received data with ${resultData.campaigns?.length || 0} campaigns for main view`);
          
          // Update state with new data
          setReportData(response.originalRevenueData || null);
          setFacebookCostData(response.facebookCostData || null);
          setAdSenseData(response.adSenseData || null);
          setCompadoData(response.compadoData || null);
          setCombinedData(resultData);

          console.log(`✅ MAIN VIEW DATA SET: ${resultData?.campaigns?.length || 0} campaigns for account: ${facebookAccountId || 'none'}`);
          
          paginationAndSorting.updateTotalItems(response.totalItems || 0);
          
          const isAllDataFetched = paginationAndSorting.checkAllDataFetched(
            resultData.campaigns ? resultData.campaigns.length : 0,
            response.totalItems
          );
          
          if (isAllDataFetched) {
            setAllData({
              ...resultData,
              campaigns: [...resultData.campaigns]
            });
          }
          
          return { success: true, data: resultData };
        } else {
          setError(response.error || 'Failed to fetch report data');
          showError(response.error || 'Failed to fetch report data');
          return { success: false, error: response.error };
        }
      } else {
        // Normal case - not returning to main view
        const response = await fetchCampaignData(
          startDate,
          endDate,
          filters,
          effectiveViewState,
          { sortField, sortDirection },
          { page, rowsPerPage },
          forceRefresh,
          facebookCampaigns,
          facebookAccountId
        );
        
        if (response.success) {
          // Determine if we need to apply client-side sorting for certain fields
          let resultData = response.data;
          
          // Client-side sorting for fields like 'cost', 'profit', 'roas' which are calculated after
          // combining revenue and cost data
          if (response.data.needsClientSideSorting && response.data.clientSortField) {
            console.log(`Applying client-side sorting for ${response.data.clientSortField} ${response.data.clientSortDirection}`);
            resultData = handleClientSideSorting(
              response.data, 
              response.data.clientSortField, 
              response.data.clientSortDirection
            );
          }
          
          // Remove duplicates
          resultData = removeDuplicates(resultData);
          
          console.log(`Received data with ${resultData.campaigns?.length || 0} campaigns`);
          
          // Precache campaign types for better performance
          if (resultData.campaigns && resultData.campaigns.length > 0) {
            console.log('Pre-caching campaign types for better performance');
            precacheCampaignTypes(resultData.campaigns, taboolaCampaigns, facebookCampaigns);
          }
          
          // Update state with new data
          setReportData(response.originalRevenueData || null);
          setFacebookCostData(response.facebookCostData || null);
          setAdSenseData(response.adSenseData || null);
          setCompadoData(response.compadoData || null);
          setCombinedData(resultData);

          console.log(`✅ NORMAL VIEW DATA SET: ${resultData?.campaigns?.length || 0} campaigns for account: ${facebookAccountId || 'none'}`);
          
          paginationAndSorting.updateTotalItems(response.totalItems || 0);
          
          // Check if all data is fetched
          const isAllDataFetched = paginationAndSorting.checkAllDataFetched(
            resultData.campaigns ? resultData.campaigns.length : 0,
            response.totalItems
          );
          
          // Update all data cache if needed
          if (isAllDataFetched) {
            setAllData({
              ...resultData,
              campaigns: [...resultData.campaigns]
            });
          }
          
          return { success: true, data: resultData };
        } else {
          setError(response.error || 'Failed to fetch report data');
          showError(response.error || 'Failed to fetch report data');
          return { success: false, error: response.error };
        }
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      setError(error.message || 'An unexpected error occurred');
      showError(error.message || 'An unexpected error occurred');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };
  
  // Sort data client-side when needed
  const handleClientSideSorting = (combinedData, sortField, sortDirection) => {
    if (!combinedData || !combinedData.campaigns || !combinedData.campaigns.length) {
      return combinedData;
    }
    
    console.log(`Performing client-side sorting on ${sortField} ${sortDirection}`);
    console.log(`Data range: ${combinedData.start_date} to ${combinedData.end_date}`);
    
    // Check if we're dealing with multiple days
    const isMultiDayData = combinedData.start_date !== combinedData.end_date;
    if (isMultiDayData) {
      console.log('Sorting data across multiple days');
    }
    
    // Check if we're in subid3 view by examining data structure
    const isSubid3View = combinedData.campaigns.some(c => c.subid3 || c.publisher_id);
    if (isSubid3View) {
      console.log('Detected subid3 view data - applying specialized sorting logic');
    }
    
    // Create a copy to avoid modifying the original
    let sortedCampaigns = [...combinedData.campaigns];
    
    // Create a Map for precomputed values to improve sort performance
    const sortValues = new Map();
    
    // Pre-compute all values for sorting
    sortedCampaigns.forEach(campaign => {
      // Skip campaigns without proper ID
      if (!campaign.campaign_id) return;
      
      // Handle specific field types - with enhanced processing for subid3 data
      let value;
      
      switch (sortField) {
        case 'cost':
          // Ensure we're always dealing with numbers, handle invalid values
          value = campaign.cost !== undefined && campaign.cost !== null && !isNaN(campaign.cost) ? 
            parseFloat(campaign.cost) : 0;
          break;
        case 'profit':
          value = campaign.profit !== undefined && campaign.profit !== null && !isNaN(campaign.profit) ? 
            parseFloat(campaign.profit) : 0;
          break;
        case 'roas':
          value = campaign.roas !== undefined && campaign.roas !== null && !isNaN(campaign.roas) ? 
            parseFloat(campaign.roas) : 0;
          break;
        case 'payout':
          value = campaign.payout !== undefined && campaign.payout !== null && !isNaN(campaign.payout) ? 
            parseFloat(campaign.payout) : 0;
          break;
        case 'ctr':
          // Handle percentage values - strip '%' if present and convert to float
          value = typeof campaign.ctr === 'string' && campaign.ctr.includes('%') ? 
            parseFloat(campaign.ctr.replace('%', '')) : 
            (campaign.ctr !== undefined && campaign.ctr !== null && !isNaN(campaign.ctr) ? parseFloat(campaign.ctr) : 0);
          break;
        case 'revenue':
          value = campaign.revenue !== undefined && campaign.revenue !== null && !isNaN(campaign.revenue) ? 
            parseFloat(campaign.revenue) : 0;
          break;
        case 'impressions':
          // Handle string formatted numbers with commas
          value = typeof campaign.impressions === 'string' ? 
            parseInt(campaign.impressions.replace(/,/g, ''), 10) : 
            (campaign.impressions !== undefined && campaign.impressions !== null ? parseInt(campaign.impressions, 10) : 0);
          break;
        case 'clicks':
          // Handle string formatted numbers with commas
          value = typeof campaign.clicks === 'string' ? 
            parseInt(campaign.clicks.replace(/,/g, ''), 10) : 
            (campaign.clicks !== undefined && campaign.clicks !== null ? parseInt(campaign.clicks, 10) : 0);
          break;
        case 'estimated_revenue':
          value = campaign.estimated_revenue !== undefined && campaign.estimated_revenue !== null && !isNaN(campaign.estimated_revenue) ? 
            parseFloat(campaign.estimated_revenue) : 0;
          break;
        case 'campaign_id':
          // For subid3 view, ensure we get the proper ID (might be stored in different fields)
          if (isSubid3View) {
            value = campaign.subid3 || campaign.publisher_id || campaign.campaign_id || '';
          } else {
            value = campaign.campaign_id || '';
          }
          break;
        default:
          // For string fields or default fallback
          // Safely handle null/undefined values for strings
          value = campaign[sortField] !== undefined && campaign[sortField] !== null ? campaign[sortField] : '';
          
          // Handle money values that might have $ prefix
          if (typeof value === 'string' && value.startsWith('$')) {
            value = parseFloat(value.substring(1));
          }
      }
      
      // Store precomputed value
      sortValues.set(campaign, value);
    });
    
    // Sort using precomputed values for performance
    sortedCampaigns.sort((a, b) => {
      const valA = sortValues.get(a);
      const valB = sortValues.get(b);
      
      // Skip undefined values
      if (valA === undefined || valB === undefined) return 0;
      
      // For string comparison
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc' 
          ? valA.localeCompare(valB) 
          : valB.localeCompare(valA);
      }
      
      // For numeric comparison - handle NaN values
      if (isNaN(valA)) return sortDirection === 'asc' ? -1 : 1;
      if (isNaN(valB)) return sortDirection === 'asc' ? 1 : -1;
      
      // Standard numeric comparison
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });
    
    // Log first few items after sorting for debugging
    console.log('First 3 items after client-side sorting:', 
      sortedCampaigns.slice(0, 3).map(c => ({
        id: c.campaign_id,
        value: c[sortField]
      }))
    );
    
    return {
      ...combinedData,
      campaigns: sortedCampaigns
    };
  };
  
  // Function to download data as CSV using current table data
  const handleDownloadCSV = () => {
    if (!combinedData || !combinedData.campaigns || combinedData.campaigns.length === 0) {
      showError('No data available to export');
      return false;
    }
    
    console.log('=== CSV EXPORT DEBUG ===');
    console.log('Combined data campaigns count:', combinedData.campaigns.length);
    console.log('First 3 campaigns for debugging:', combinedData.campaigns.slice(0, 3));
    
    try {
      // Get the sorted data currently shown in the table
      const sortedData = handleClientSideSorting(combinedData, sortField, sortDirection);
      const campaigns = sortedData.campaigns;
      
      // Create CSV headers based on the view - include all columns shown in table
      const headers = viewState.isSubid3View 
        ? ['Campaign ID', 'Campaign Name', 'Sub ID 2', 'Sub ID 3', 'Impressions', 'Clicks', 'CVR (%)', 'Revenue', 'Cost', 'Profit', 'ROAS', 'EPC']
        : ['Campaign ID', 'Campaign Name', 'Sub ID 2', 'Impressions', 'Clicks', 'CVR (%)', 'Revenue', 'Cost', 'Profit', 'ROAS', 'EPC', 'Daily Budget'];
      
      // Create CSV rows from current campaign data
      const csvRows = [
        headers.join(','),
        ...campaigns.map(campaign => {
          // Use pre-calculated values from the combined data instead of recalculating
          const revenue = campaign.revenue || 0;
          const cost = campaign.cost || 0;
          const profit = campaign.profit || 0;
          const roas = campaign.roas || 0;
          const epc = campaign.payout || 0; // EPC is stored as 'payout' in the combined data
          
          // Get budget using the EXACT same logic as the table
          let budgetValue = 0;

          // Use the same function that the table uses for Facebook campaigns
          const fbCampaign = getFacebookCampaignData(campaign.campaign_id, combinedData, facebookCampaigns);
          if (fbCampaign) {
            // Use extractFacebookBudget function like in the table
            const budget = extractFacebookBudget(fbCampaign, campaign);
            budgetValue = budget || 0;
          } else {
            // Fallback
            budgetValue = campaign.daily_budget || 0;
          }
          
          // Extra fallback: if we still don't have a budget value, try to get it from the campaign object directly
          if (!budgetValue || budgetValue === 0) {
            // Try all possible budget field names
            budgetValue = campaign.daily_budget || 
                         campaign.daily_cap || 
                         campaign.budget ||
                         (campaign.lifetime_budget ? campaign.lifetime_budget / 30 : 0) ||
                         0;
          }
          
          // Debug log for first campaign
          if (campaigns.indexOf(campaign) === 0) {
            console.log('=== FIRST CAMPAIGN BUDGET DEBUG ===');
            console.log('Campaign ID:', campaign.campaign_id);
            console.log('Platform:', campaign.platform);
            console.log('facebookCampaigns available:', Object.keys(facebookCampaigns).length);

            const debugFbCampaign = getFacebookCampaignData(campaign.campaign_id, combinedData, facebookCampaigns);
            console.log('Facebook campaign data:', debugFbCampaign);
            if (debugFbCampaign) {
              const budget = extractFacebookBudget(debugFbCampaign, campaign);
              console.log('Extracted Facebook budget:', budget);
            }

            console.log('Final budgetValue:', budgetValue);
            console.log('CSV row data will be:', {
              campaign_id: campaign.campaign_id,
              revenue: revenue.toFixed(2),
              cost: cost.toFixed(2),
              profit: profit.toFixed(2),
              roas: roas.toFixed(2),
              epc: epc.toFixed(3),
              budget: budgetValue > 0 ? budgetValue.toFixed(2) : 'EMPTY'
            });
          }
          
          const row = viewState.isSubid3View 
            ? [
                `"${(campaign.campaign_id || '').replace(/"/g, '""')}"`,
                `"${(campaign.campaign_name || '').replace(/"/g, '""')}"`,
                `"${(campaign.subid_2 || '').replace(/"/g, '""')}"`,
                `"${(campaign.subid_3 || '').replace(/"/g, '""')}"`,
                campaign.impressions || 0,
                campaign.clicks || 0,
                (campaign.ctr || 0).toFixed(2),
                revenue.toFixed(2),
                cost.toFixed(2),
                profit.toFixed(2),
                roas.toFixed(2),
                epc.toFixed(3)
              ]
            : [
                `"${(campaign.campaign_id || '').replace(/"/g, '""')}"`,
                `"${(campaign.campaign_name || '').replace(/"/g, '""')}"`,
                `"${(campaign.subid_2 || '').replace(/"/g, '""')}"`,
                campaign.impressions || 0,
                campaign.clicks || 0,
                (campaign.ctr || 0).toFixed(2),
                revenue.toFixed(2),
                cost.toFixed(2),
                profit.toFixed(2),
                roas.toFixed(2),
                epc.toFixed(3),
(budgetValue > 0 ? budgetValue.toFixed(2) : '')
              ];
          return row.join(',');
        })
      ];
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rsoc_analytics_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('CSV exported successfully with', campaigns.length, 'campaigns');
      return true;
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showError(error.message || 'Failed to export data');
      return false;
    }
  };
  
  // Reset data state
  const resetDataState = () => {
    console.log('Resetting campaign data state - preserving view state');
    setReportData(null);
    setCombinedData(null);
    setAllData(null);
    setError(null);
    
    // Clear campaign type cache when data state is reset
    clearCampaignTypeCache();
    
    // Intentionally NOT resetting any view state properties
    console.log('Data state reset complete');
  };
  
  // Function to update a single campaign's status locally (without full refresh)
  const updateCampaignStatusLocally = (campaignId, platform, newIsActive) => {
    console.log(`Updating campaign ${campaignId} status locally to ${newIsActive ? 'active' : 'paused'}`);

    // All campaigns are Facebook now
    setFacebookCampaigns(prev => {
      if (!prev[campaignId]) return prev;
      return {
        ...prev,
        [campaignId]: {
          ...prev[campaignId],
          is_active: newIsActive,
          status: newIsActive ? 'ACTIVE' : 'PAUSED',
          effective_status: newIsActive ? 'ACTIVE' : 'PAUSED'
        }
      };
    });
  };

  // Function to update a single campaign's budget locally (without full refresh)
  const updateCampaignBudgetLocally = (campaignId, platform, newBudget) => {
    console.log(`Updating campaign ${campaignId} budget locally to $${newBudget}`);
    const budgetValue = parseFloat(newBudget);

    // Update Facebook campaigns
    setFacebookCampaigns(prev => {
      if (!prev[campaignId]) return prev;
      return {
        ...prev,
        [campaignId]: {
          ...prev[campaignId],
          daily_budget: budgetValue
        }
      };
    });

    // Also update combinedData if it exists
    setCombinedData(prev => {
      if (!prev || !Array.isArray(prev)) return prev;
      return prev.map(item => {
        if (item.campaign_id === campaignId) {
          return {
            ...item,
            daily_budget: budgetValue,
            ...(item.fbCampaign && {
              fbCampaign: {
                ...item.fbCampaign,
                daily_budget: budgetValue
              }
            }),
            ...(item.taboolaCampaign && {
              taboolaCampaign: {
                ...item.taboolaCampaign,
                daily_cap: budgetValue
              }
            })
          };
        }
        return item;
      });
    });
  };

  // Function to clear specifically Facebook data
  const clearFacebookData = () => {
    console.log('Clearing Facebook-specific data completely');
    
    // Clear all Facebook-related state
    setFacebookCampaigns({});
    setFacebookCostData(null);
    
    // Clear ALL combined data to prevent mixing old and new data
    setCombinedData(null);
    setAllData(null);
    setReportData(null);
    
    // Clear campaign type cache to remove Facebook campaign type mappings
    clearCampaignTypeCache();
    
    console.log('Facebook data completely cleared');
  };
  
  // Get combined data - simplified since we're loading all accounts together
  const getValidCombinedData = () => {
    return combinedData;
  };
  
  return {
    loading,
    error,
    reportData,
    facebookCostData,
    adSenseData,
    compadoData,
    facebookCampaigns,
    combinedData,
    allData,
    summaryMetrics,
    setLoading,
    loadCampaignsData,
    loadFacebookCampaignsData,
    fetchReport,
    fetchReportWithViewState,
    handleClientSideSorting,
    handleDownloadCSV,
    resetDataState,
    setCombinedData,
    clearFacebookData,
    getValidCombinedData,
    updateCampaignStatusLocally,
    updateCampaignBudgetLocally,
    // Deprecated - kept for backwards compatibility
    taboolaCostData,
    taboolaCampaigns,
  };
};

export default useCampaignData; 